import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { schoolContext } from '@/core/api';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * CLIENT STORE CONVENTIONS (this is the reference example — see CLAUDE.md §2.3)
 * ───────────────────────────────────────────────────────────────────────────
 * - Zustand holds **client/UI/session state only**. Server data lives in
 *   TanStack Query, never here.
 * - One store per concern; expose selector hooks (`useSession((s) => s.x)`) so
 *   components only re-render on the slices they read.
 * - Persist **narrowly** via `partialize` — only durable, non-sensitive prefs.
 *   Never persist `user`/`role`/`isAuthenticated` (re-fetched on bootstrap;
 *   trusting them from storage is a staleness/security footgun) or tokens
 *   (those live in `tokenStorage`; the access token is deliberately in-memory).
 * - Side effects that must mirror state (e.g. the `X-School` header) go in
 *   the action, not in components.
 * ───────────────────────────────────────────────────────────────────────────
 */

/** Roles mirror the backend RBAC set exactly (users.models.SchoolRole).
 *  One role per (school, user) membership — never a list. */
export type Role = 'admin' | 'teacher' | 'pupil' | 'parent';

export const ROLES: readonly Role[] = ['admin', 'teacher', 'pupil', 'parent'] as const;

/**
 * Strictly narrow a backend role string to the UI union. Unknown values map to
 * `null` (deny-by-default) — never guess a fallback role on a platform holding
 * minors' data.
 */
export function normalizeRole(value?: string | null): Role | null {
  const v = value?.toLowerCase().trim();
  return (ROLES as readonly string[]).includes(v ?? '') ? (v as Role) : null;
}

/** Mirrors SchoolSerializer (osvitly/apps/schools/serializers.py). */
export interface School {
  id: number;
  /** URL handle for `/api/schools/{public_id}/` (backend ADR-004/016). The
   *  `slug` stays the tenant key carried by `X-School`. */
  public_id: string;
  name: string;
  slug: string;
  type: 'organization' | 'individual';
  default_language: 'en' | 'uk';
  /** Display preset for grades (backend ADR-015) — scores are always points. */
  grading_scale: 'points' | 'ua_12';
  timezone: string;
  /** Logo URL (relative path served by the backend); null if unset. */
  logo: string | null;
  /**
   * Child-safety switch (backend ADR-020): pupil↔pupil direct messages are OFF
   * until an admin of this school turns them on. Read-only here; written
   * through the school settings PATCH.
   */
  allow_pupil_direct_messages: boolean;
  is_active: boolean;
}

/** One row of `GET /api/schools/mine/` — a school + the caller's role in it. */
export interface Membership {
  school: School;
  role: Role;
}

/** Minimal linked-child info (users.MemberChildSerializer — C3: least pupil PII). */
export interface MemberChild {
  public_id: string;
  first_name: string;
  last_name: string;
}

/** One row of the current-user `memberships` (users.MembershipSerializer).
 *  Distinct from {@link Membership} (which comes from `/schools/mine/`): this
 *  one carries the parent's linked `children`. */
export interface UserMembership {
  id: number;
  /** The caller's OWN Member public_id in that school (backend ADR-016). */
  public_id: string;
  school_slug: string;
  school_name: string;
  role: string;
  is_active: boolean;
  /** Linked children — populated for parent memberships, empty otherwise. */
  children: MemberChild[];
}

/** Mirrors CurrentUserSerializer (osvitly/apps/users/serializers.py). */
export interface CurrentUser {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  timezone: string | null;
  photo: string | null;
  memberships: UserMembership[];
}

interface SessionPayload {
  user: CurrentUser;
  memberships: Membership[];
  activeSchoolSlug: string | null;
}

interface SessionState {
  user: CurrentUser | null;
  memberships: Membership[];
  /** Persisted across reloads (see partialize) so we land in the same school. */
  activeSchoolSlug: string | null;
  /** The caller's single role in the ACTIVE school — derived, never persisted. */
  role: Role | null;
  isAuthenticated: boolean;
  setSession: (payload: SessionPayload) => void;
  setActiveSchool: (slug: string) => void;
  clear: () => void;
}

const roleFor = (memberships: Membership[], slug: string | null): Role | null =>
  memberships.find(m => m.school.slug === slug)?.role ?? null;

/**
 * The caller's OWN Member `public_id` in the active school — the id every
 * "this is me" surface needs (`/progress/me`, matching oneself against a
 * class's `teachers[]`). It comes straight from the current-user payload
 * (`users.MembershipSerializer`, backend ADR-016), so no extra request and no
 * inference from unrelated data.
 */
const myMemberPublicIdFor = (state: SessionState): string | null =>
  state.user?.memberships.find(m => m.is_active && m.school_slug === state.activeSchoolSlug)
    ?.public_id ?? null;

export const useSession = create<SessionState>()(
  persist(
    (set, get) => ({
      user: null,
      memberships: [],
      activeSchoolSlug: null,
      role: null,
      isAuthenticated: false,

      setSession: ({ user, memberships, activeSchoolSlug }) => {
        schoolContext.set(activeSchoolSlug); // keep the X-School header in sync
        set({
          user,
          memberships,
          activeSchoolSlug,
          role: roleFor(memberships, activeSchoolSlug),
          isAuthenticated: true,
        });
      },

      setActiveSchool: slug => {
        schoolContext.set(slug);
        set({ activeSchoolSlug: slug, role: roleFor(get().memberships, slug) });
      },

      clear: () => {
        schoolContext.set(null);
        set({
          user: null,
          memberships: [],
          activeSchoolSlug: null,
          role: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'osvitly.session',
      // Persist ONLY the active-school preference — nothing sensitive or server-owned.
      partialize: s => ({ activeSchoolSlug: s.activeSchoolSlug }),
    }
  )
);

/** Selector hook for {@link myMemberPublicIdFor}. Null until bootstrap. */
export const useMyMemberPublicId = (): string | null => useSession(myMemberPublicIdFor);
